import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wind, Sparkles, ChevronRight, Coffee, Focus, Zap, Share2 } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import ShareSheet from '../components/ShareSheet';

// Curated collection: quick resets tuned for the workday. Each entry maps
// directly to something that already exists in the app (breathing exercise,
// meditation post, or affirmation theme) so this page is a pure remix, not
// new content the CMS has to maintain.
const QUICK_RESETS = [
  {
    id: 'box-breath',
    title: 'Box Breath',
    tagline: '90 seconds to reset before your next meeting',
    accent: '#173F35',
    Icon: Wind,
    to: '/workplace-reset/box-breath',
  },
  {
    id: 'shoulder-release',
    title: 'Shoulder Release',
    tagline: 'Loosen where the day has been holding on',
    accent: '#789B83',
    Icon: Focus,
    to: '/workplace-reset/shoulder-release',
  },
  {
    id: 'workday-release',
    title: 'Workday Release',
    tagline: '4 slow exhales to close the laptop cleanly',
    accent: '#D6AD60',
    Icon: Coffee,
    to: '/workplace-reset/workday-release',
  },
  {
    id: 'energizer',
    title: '5-in / 5-out Energizer',
    tagline: 'Sharpen focus without the second coffee',
    accent: '#5B7A65',
    Icon: Zap,
    to: '/workplace-reset/energizer',
  },
];

const AFFIRMATION_LINKS = [
  { id: 'discipline', title: 'Discipline', to: '/manifest/discipline' },
  { id: 'purpose',    title: 'Purpose',    to: '/manifest/purpose' },
  { id: 'self-worth', title: 'Self Worth', to: '/manifest/self-worth' },
];

const WorkplaceResetPage = () => {
  const { posts } = useContent();
  // Share affordance for the meditation list below.
  const [sharePost, setSharePost] = useState(null);
  const shareContent = (p) => ({
    kind: 'post',
    id: p.id,
    title: p.title,
    text: `"${p.title}" — a reflection on Reflections by Rowdy`,
    url: typeof window !== 'undefined' ? `${window.location.origin}/post/${p.id}` : '',
    recommendable: true,
  });

  // Meditations from Anxiety + Guided that fit a workday break — anything
  // ≤6 minutes, plus any without a stated duration (we assume the shorter,
  // curated posts by default so the "quick reset" bucket isn't empty when
  // metadata is thin).
  const shortMeditations = React.useMemo(() => {
    if (!posts?.length) return [];
    return posts
      .filter((p) => (p.categoryId === 'guided-meditations' || p.categoryId === 'anxiety'))
      .filter((p) => {
        const m = String(p.duration || '').match(/\d+/);
        if (!m) return true;                       // no duration listed → include
        return parseInt(m[0], 10) <= 6;
      })
      .slice(0, 4);
  }, [posts]);

  return (
    <div className="pb-8" data-testid="workplace-reset-page">
      {/* Hero */}
      <div className="px-5 pt-6 pb-4 animate-fade-in">
        <p className="rbr-eyebrow mb-2">Workplace Reset</p>
        <h1 className="rbr-heading text-3xl mb-1 leading-tight">
          Between meetings.
        </h1>
        <p className="text-[#C4D3C9] text-sm">
          Short resets you can do at your desk — nobody has to know.
        </p>
      </div>

      {/* Quick Resets — breathing patterns tuned for the workday */}
      <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
        <p className="rbr-eyebrow mb-3">2-Minute Resets</p>
        <div className="grid grid-cols-2 gap-3">
          {QUICK_RESETS.map((q) => {
            const Icon = q.Icon;
            return (
              <Link
                key={q.id}
                to={q.to}
                data-testid={`workplace-reset-${q.id}`}
                className="flex flex-col gap-2 p-4 rounded-2xl bg-[#1F4C40] border border-[#2E6353] hover:border-[#D6AD60]/60 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${q.accent}22`, boxShadow: `inset 0 0 0 1px ${q.accent}55` }}
                >
                  <Icon className="w-4 h-4" style={{ color: q.accent }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#F8F6F0] leading-tight mb-0.5">{q.title}</p>
                  <p className="text-[11px] text-[#A3BEAF] leading-snug">{q.tagline}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Short meditations — ≤6 min so they fit an actual break */}
      {shortMeditations.length > 0 && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
          <p className="rbr-eyebrow mb-3">Meditations Under 6 Minutes</p>
          <div className="space-y-2">
            {shortMeditations.map((p) => (
              <div key={p.id} className="relative">
                <Link
                  to={`/post/${p.id}`}
                  data-testid={`workplace-meditation-${p.id}`}
                  className="flex items-center gap-3 bg-[#1F4C40] border border-[#2E6353] rounded-2xl p-3 pr-14 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  {p.image ? (
                    <img
                      src={p.image}
                      alt=""
                      loading="lazy"
                      className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-[#E8DDCB] flex items-center justify-center flex-shrink-0">
                      <Wind className="w-4 h-4 text-[#D6AD60]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F8F6F0] truncate">{p.title}</p>
                    <p className="text-[11px] text-[#A3BEAF] truncate">
                      {p.category}{p.duration ? ` · ${p.duration}` : ''}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#7A9B8C] flex-shrink-0" />
                </Link>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSharePost(p); }}
                  data-testid={`workplace-share-${p.id}`}
                  aria-label={`Share ${p.title}`}
                  title="Share"
                  className="absolute right-9 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-[#173F35] hover:bg-[#D6AD60]/20 border border-[#2E6353] transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" style={{ color: '#D6AD60' }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Affirmations for hard days at work */}
      <div className="px-5 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
        <p className="rbr-eyebrow mb-3">Read Between the Slack Messages</p>
        <div className="grid grid-cols-3 gap-3">
          {AFFIRMATION_LINKS.map((a) => (
            <Link
              key={a.id}
              to={a.to}
              data-testid={`workplace-affirm-${a.id}`}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-gradient-to-br from-[#D6AD60]/15 to-[#789B83]/15 border border-[#D6AD60]/30 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <Sparkles className="w-4 h-4 text-[#D6AD60]" />
              <span className="text-xs font-semibold text-[#F8F6F0] text-center leading-tight">
                {a.title}
              </span>
            </Link>
          ))}
        </div>
      </div>

      <ShareSheet
        open={!!sharePost}
        onClose={() => setSharePost(null)}
        content={sharePost ? shareContent(sharePost) : undefined}
      />
    </div>
  );
};

export default WorkplaceResetPage;
